/**
 * Segment registry for the customer list — pairs each `CustomerSegment` with its
 * tab label, count key, column list (from `customer-column-definitions`) and
 * Filters fields. Ordered exactly as the legacy tab bar. Deleted is admin-only.
 */
import type { FilterField } from '@vnetwork/ui/data';
import type { CustomerCounts, CustomerSegment } from '@/api/customers';
import { INDUSTRY_OPTIONS, SOURCE_OPTIONS } from './customer-filter-constants';
import {
  activeColumns,
  cancelledColumns,
  deadLeadColumns,
  deletedColumns,
  journeyColumns,
  trialColumns,
  type Col,
} from './customer-column-definitions';

/* ── Filters ──────────────────────────────────────────────────────────────── */

const industryFilter: FilterField = {
  value: 'customer_industry',
  label: 'Industry',
  input: 'multiselect',
  options: INDUSTRY_OPTIONS,
};

const sourceFilter: FilterField = {
  value: 'customer_source',
  label: 'Source',
  input: 'multiselect',
  options: SOURCE_OPTIONS,
};

/**
 * Name is NOT a FilterBuilder field — it is searched via the toolbar `q` param.
 * Putting `customer_name` in the base64 `filters` payload matches nothing on the
 * backend, so it is intentionally absent from every filter list below.
 */

/** Journey/lead segments: industry + source. */
const journeyFilters: FilterField[] = [industryFilter, sourceFilter];
/** Service segments (active/trial/stop): industry. */
const serviceFilters: FilterField[] = [industryFilter];

/* ── Segment registry ─────────────────────────────────────────────────────── */

export interface SegmentConfig {
  segment: CustomerSegment;
  label: string;
  /** Key on `CustomerCounts` for the tab badge. */
  countKey: keyof CustomerCounts;
  columns: Col[];
  filterFields: FilterField[];
  searchPlaceholder: string;
  /** Deleted is admin-only. */
  adminOnly?: boolean;
}

/** Ordered exactly as the legacy tab bar (QP · Cool · Active · Trial · Stop · Dead · Deleted). */
export const CUSTOMER_SEGMENTS: SegmentConfig[] = [
  {
    segment: 'qualified-prospects',
    label: 'Qualified Prospects',
    countKey: 'qualified_prospects',
    columns: journeyColumns,
    filterFields: journeyFilters,
    searchPlaceholder: 'Search prospects…',
  },
  {
    segment: 'cool-lead',
    label: 'Cool Leads',
    countKey: 'cool_leads',
    columns: journeyColumns,
    filterFields: journeyFilters,
    searchPlaceholder: 'Search cool leads…',
  },
  {
    segment: 'active',
    label: 'Active',
    countKey: 'active',
    columns: activeColumns,
    filterFields: serviceFilters,
    searchPlaceholder: 'Search active customers…',
  },
  {
    segment: 'trial',
    label: 'Trial',
    countKey: 'trial',
    columns: trialColumns,
    filterFields: serviceFilters,
    searchPlaceholder: 'Search trial customers…',
  },
  {
    segment: 'cancelled',
    label: 'Stop',
    countKey: 'cancelled',
    columns: cancelledColumns,
    filterFields: serviceFilters,
    searchPlaceholder: 'Search stopped customers…',
  },
  {
    segment: 'dead-lead',
    label: 'Dead Lead',
    countKey: 'dead_leads',
    columns: deadLeadColumns,
    filterFields: journeyFilters,
    searchPlaceholder: 'Search dead leads…',
  },
  {
    segment: 'deleted',
    label: 'Deleted',
    countKey: 'deleted',
    columns: deletedColumns,
    // No filterable fields (name is `q`-searched) → Filters popover auto-hides.
    filterFields: [],
    searchPlaceholder: 'Search deleted customers…',
    adminOnly: true,
  },
];
